import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Weekday } from 'rrule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { DatePickerField, DaysOfWeek, DropdownField, Text } from '@frontend/design-system';
import { AvailabilityFormRow } from '../../../../components/availability/availability-form/atoms/availability-form-row';
import { DayPickerWithSelectedDays } from '../../../../components/availability/availability-form/atoms/day-picker-with-selected-days';
import { MonthlyRecurrenceRow } from '../../../../components/availability/availability-form/atoms/monthly-recurrence-row';
import { PreviewNotification } from '../../../../components/availability/availability-form/atoms/preview-notification';
import { RepeatFrequencyDropdown } from '../../../../components/availability/availability-form/atoms/repeat-frequency-dropdown';
import {
  getPreviewPatternDescription,
  PreviewPatternFormValues,
} from '../../../../components/availability/availability-form/helpers/get-preview-pattern-description';
import {
  availabilityRecurrenceOptions,
  repeatFrequencyOptions,
} from '../../../../components/availability/availability-form/types';
import { useRuleRecurrenceForm } from '../../../../hooks/settings/use-rule-recurrence-form';
import { getRRuleDayFromDaysOfWeek } from '../../../../rrule-helper';
import { GetRuleExceptionFormFieldProps, RuleExceptionFormValues } from '../hooks/use-create-edit-rule-exception-form';
import { ExceptionTimeRangeField } from './exception-time-range-field';

type Props = {
  formProps: ReturnType<typeof useRuleRecurrenceForm<GetRuleExceptionFormFieldProps, RuleExceptionFormValues>>;
  locationId: string;
};

export const RecurrenceForm = ({ locationId, formProps }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { locationTimezoneMap } = SchedulerV3.Helpers.useScheduleLocationTimezoneShallowStore('locationTimezoneMap');
  const timezone = locationTimezoneMap[locationId || '']?.timezone;

  const {
    recurrenceFormValues,
    recurrenceType,
    daysOfWeek,
    repeatOn,
    rRule,
    formValues,
    setDaysOfWeek,
    setRepeatOn,
    getRecurrenceFormFieldProps,
    getMonthlyRecurrenceFormFieldProps,
    getAvailabilityRuleFieldProps: getFieldProps,
  } = formProps;

  const { repeatFrequencyType } = recurrenceFormValues;
  const previewPatternFormValues = useMemo<PreviewPatternFormValues>(
    () => ({
      startDate: formValues.startDate,
      endDate: formValues.endDate,
      startTime: formValues.isAllDay ? null : formValues.timeRange?.[0] ?? '',
      endTime: formValues.isAllDay ? null : formValues.timeRange?.[1] ?? '',
    }),
    [formValues.startDate, formValues.endDate, formValues.isAllDay, formValues.timeRange]
  );

  const previewPatternDescription = useMemo(
    () =>
      getPreviewPatternDescription({
        recurrenceType,
        repeatFrequencyType,
        rRule,
        formValues: previewPatternFormValues,
        timezone,
      }),
    [recurrenceType, repeatFrequencyType, rRule, previewPatternFormValues, timezone]
  );

  const handleDayToggle =
    (setState: React.Dispatch<React.SetStateAction<Weekday[]>>) =>
    ({ selectedDays }: { selectedDays: DaysOfWeek[] }) => {
      setState(selectedDays.map(getRRuleDayFromDaysOfWeek));
    };

  return (
    <>
      <AvailabilityFormRow>
        <DropdownField {...getFieldProps('recurrenceType')} label={t('Recurrence')} hasNonFloatingLabel>
          {availabilityRecurrenceOptions?.map((item) => (
            <DropdownField.Option key={item.id} value={item.id}>
              {item.displayName}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </AvailabilityFormRow>
      {recurrenceType === 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField
            {...getFieldProps('startDate')}
            label={t('Date')}
            css={{ maxWidth: 'none' }}
            hasNonFloatingLabel
          />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'WEEKLY' && (
        <AvailabilityFormRow title={t('Day(s) of the week')}>
          <DayPickerWithSelectedDays daysPicked={daysOfWeek} onDayToggle={handleDayToggle(setDaysOfWeek)} />
        </AvailabilityFormRow>
      )}
      {recurrenceType === 'CUSTOM' && (
        <>
          <AvailabilityFormRow title={t('Repeat Pattern')} css={repeatPatternRowStyles}>
            <Text>{t('Every')}</Text>
            <RepeatFrequencyDropdown getFieldProps={getRecurrenceFormFieldProps} />
            <DropdownField label={''} {...getRecurrenceFormFieldProps('repeatFrequencyType')}>
              {repeatFrequencyOptions?.map((item) => (
                <DropdownField.Option key={item.id} value={item.id}>
                  {item.displayName}
                </DropdownField.Option>
              ))}
            </DropdownField>
          </AvailabilityFormRow>
          {recurrenceFormValues.repeatFrequencyType === 'weeks' && (
            <AvailabilityFormRow title={t('Repeat On')}>
              <DayPickerWithSelectedDays daysPicked={repeatOn} onDayToggle={handleDayToggle(setRepeatOn)} />
            </AvailabilityFormRow>
          )}
          {recurrenceFormValues.repeatFrequencyType === 'months' && (
            <MonthlyRecurrenceRow getFieldProps={getMonthlyRecurrenceFormFieldProps} />
          )}
        </>
      )}
      {recurrenceType !== 'SPECIFIC_DATE' && (
        <AvailabilityFormRow>
          <DatePickerField label={t('Start Date')} {...getFieldProps('startDate')} />
          <DatePickerField label={t('End Date')} {...getFieldProps('endDate')} />
        </AvailabilityFormRow>
      )}
      <AvailabilityFormRow>
        <ExceptionTimeRangeField getFieldProps={getFieldProps} />
      </AvailabilityFormRow>
      {recurrenceType === 'CUSTOM' && (
        <AvailabilityFormRow>
          <PreviewNotification title={t('Pattern preview:')} description={previewPatternDescription} />
        </AvailabilityFormRow>
      )}
    </>
  );
};

const repeatPatternRowStyles = css({
  display: 'flex',
  alignItems: 'center',

  '> *': {
    flexBasis: 0,
    flexShrink: 1,
  },
  '> *:last-child': {
    flex: 1,
  },
});
